import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';

/** One site as the picker shows it — names already resolved by the caller. */
export interface SitePickRow {
  oid:             string;
  siteName:        string;
  address:         string;
  keyMap:          string;
  pwsId:           string;
  samplePointId:   string;
  samplePointType: string;
  waterType:       string;
}

const COLUMNS: AppColumn<SitePickRow>[] = [
  { field: 'siteName',        header: 'SiteName',        width: 190 },
  { field: 'address',         header: 'Address',         width: 190 },
  { field: 'keyMap',          header: 'KeyMap',          width: 110 },
  { field: 'pwsId',           header: 'PSWID',           width: 110 },
  { field: 'samplePointId',   header: 'SamplePointID',   width: 130 },
  { field: 'samplePointType', header: 'SamplePointType', width: 130 },
  { field: 'waterType',       header: 'WaterType',       width: 110 },
];

/**
 * The "Sample Sites" popup behind a sample's SiteName cell — G5's
 * StationLocation lookup window: Search box top right, a SiteName · Address ·
 * KeyMap · PSWID · SamplePointID · SamplePointType · WaterType grid with a tick
 * column, OK / Cancel. One site can be chosen; clicking a row picks it at once,
 * or tick one and press OK. Built from the shared AppDialog + AppDataGrid +
 * GridSearch, like the other grid pickers.
 */
export function PreLoginSitePickerDialog({ open, sites, currentId, onPick, onClose }: {
  open: boolean;
  sites: SitePickRow[];
  /** The sample's current site, ticked when the popup opens. */
  currentId: string | null;
  onPick: (oid: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Every opening starts clean, with the sample's current site ticked.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(currentId ? new Set([currentId]) : new Set());
  }, [open, currentId]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(s => Object.values(s).some(v => String(v ?? '').toLowerCase().includes(q)));
  }, [sites, search]);

  const ticked = sites.find(s => selected.has(s.oid));

  return (
    <AppDialog
      open={open}
      title="Sample Sites"
      width={900}
      className="dlg-center"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={!ticked}
                  onClick={() => { if (ticked) onPick(ticked.oid); }}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
      </div>
      <AppDataGrid<SitePickRow>
        rows={rows}
        columns={COLUMNS}
        getRowId={s => s.oid}
        defaultPageSize={20}
        initialSort={{ key: 'siteName', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={next => {
          // Single choice: keep the box that was just ticked.
          const added = [...next].filter(id => !selected.has(id));
          setSelected(new Set(added.length ? [added[added.length - 1]!] : next));
        }}
        onRowClick={s => onPick(s.oid)}
        emptyText="No sites for this client"
      />
    </AppDialog>
  );
}
